import React from 'react';
import { Box, Typography, Card, CardContent, Checkbox, FormGroup } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { QuestionComponentProps } from '../../types';
import { CommentField } from '../CommentField';
import { OptionEmoji } from '../../../question/components/OptionEmoji';

export const MultiSelectQuestion: React.FC<QuestionComponentProps> = ({
  question,
  value,
  comment,
  language,
  onChange,
  onCommentChange,
  disabled = false,
  error,
  autoFocus = false,
}) => {
  const getLocalizedText = (textRecord: Record<string, string>): string => {
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const getSubtitleText = (): string => {
    return language === 'no' ? 'Velg alle som passer' : 'Select all that apply';
  };

  // Ensure value is always an array for multi-select
  const selectedValues = Array.isArray(value) ? value : [];

  const handleOptionToggle = (optionId: string) => {
    if (disabled) return;

    const newSelectedValues = selectedValues.includes(optionId)
      ? selectedValues.filter((id) => id !== optionId)
      : [...selectedValues, optionId];

    onChange(newSelectedValues);
  };

  const handleCardClick = (optionId: string) => {
    handleOptionToggle(optionId);
  };

  const handleKeyDown = (event: React.KeyboardEvent, optionId: string) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionToggle(optionId);
    }
  };

  const sortedOptions = question.options?.sort((a, b) => a.order - b.order) || [];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: 'center', mb: 1 }}>
        {getLocalizedText(question.title)}
      </Typography>

      <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
        {getSubtitleText()}
      </Typography>

      {question.description && (
        <Typography sx={{ textAlign: 'center', mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(question.description)}
        </Typography>
      )}

      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <FormGroup>
          {sortedOptions.map((option, index) => {
            const isSelected = selectedValues.includes(option.id);
            const optionLabel = getLocalizedText(option.label);

            return (
              <Card
                key={option.id}
                variant="outlined"
                onClick={() => handleCardClick(option.id)}
                onKeyDown={(e) => handleKeyDown(e, option.id)}
                tabIndex={0}
                role="checkbox"
                aria-checked={isSelected}
                aria-disabled={disabled}
                aria-label={optionLabel}
                sx={{
                  mb: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  transition: 'all 0.2s ease-in-out',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected
                    ? (theme) => alpha(theme.palette.primary.main, 0.12)
                    : 'background.paper',
                  '&:hover': {
                    bgcolor: disabled
                      ? undefined
                      : isSelected
                        ? (theme) => alpha(theme.palette.primary.main, 0.12)
                        : 'action.hover',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1,
                      minHeight: 68,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        tabIndex={-1}
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus first option for keyboard navigation in survey flow
                        autoFocus={autoFocus && index === 0}
                        data-testid={`option-${option.id}`}
                        sx={{ pointerEvents: 'none', p: 1 }}
                      />
                      <Box sx={{ pointerEvents: 'none', ml: 0.5 }}>{optionLabel}</Box>
                    </Box>
                    {option.emoji && (
                      <OptionEmoji emoji={option.emoji} color={option.color} size="xlarge" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </FormGroup>
      </Box>

      {question.hasCommentField && onCommentChange && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <CommentField
            value={comment}
            onChange={onCommentChange}
            language={language}
            disabled={disabled}
            placeholder={
              question.commentFieldDescription
                ? getLocalizedText(question.commentFieldDescription)
                : undefined
            }
          />
        </Box>
      )}

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 2 }}
          role="alert"
          data-testid="error-message"
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
